import type { GovernancePolicyFormData } from '@experiences/interfaces';
import React, {
    useCallback,
    useMemo,
    useRef,
} from 'react';

import { routePaths } from '../../../../common/constants/routePaths';
import { useTenantIdContext } from '../../../../common/providers/TenantIdProvider';
import { UiWizard } from '../../../common/UiWizard';
import { GovernancePolicyCreateDetailsStep } from '../steps/details/GovernancePolicyCreateDetailsStep';
import { GovernancePolicyCreateEnforcementStep } from '../steps/enforcement/GovernancePolicyCreateEnforcementStep';
import { GovernancePolicyCreatePolicyDefinitionStep } from '../steps/policy-definition/GovernancePolicyCreatePolicyDefinitionStep';
import { GovernancePolicyCreateReviewAndSaveStep } from '../steps/review/GovernancePolicyCreateReviewAndSaveStep';
import { GovernancePolicyCreateScopeStep } from '../steps/scope/GovernancePolicyCreateScopeStep';
import { GovernanceWizardTelemetryTracker } from './GovernanceWizardTelemetryTracker';

interface StepDefinition {
    stepKey: string;
    component: React.ComponentType;
}

const governancePolicyCreateSteps = (mode: 'create' | 'edit' = 'create'): StepDefinition[] => {
    const type = mode === 'edit' ? 'edit' : 'add';
    return [
        {
            stepKey: 'CLIENT_GOVERNANCE_STEP_SCOPE',
            component: () => <GovernancePolicyCreateScopeStep type={type} />,
        },
        {
            stepKey: 'CLIENT_GOVERNANCE_STEP_POLICY_DEFINITION',
            component: () => <GovernancePolicyCreatePolicyDefinitionStep />,
        },
        {
            stepKey: 'CLIENT_GOVERNANCE_STEP_DETAILS',
            component: () => <GovernancePolicyCreateDetailsStep type={type} />,
        },
        {
            stepKey: 'CLIENT_GOVERNANCE_STEP_ENFORCEMENT_MODE',
            component: () => <GovernancePolicyCreateEnforcementStep type={type} />,
        },
        {
            stepKey: 'CLIENT_GOVERNANCE_STEP_REVIEW_AND_SAVE',
            component: () => <GovernancePolicyCreateReviewAndSaveStep
                type={type}
                mode={mode} />,
        },
    ];
};

interface GovernancePolicyCreateWizardProps {
    onSubmitHandler: (data: GovernancePolicyFormData) => Promise<void>;
    initialStep?: number;
    mode?: 'create' | 'edit';
    returnUrl?: string;
}

export const GovernancePolicyCreateWizard: React.FC<GovernancePolicyCreateWizardProps> = ({
    onSubmitHandler,
    initialStep,
    mode = 'create',
    returnUrl,
}) => {
    const { tenantId: scopedTenantId } = useTenantIdContext();
    const cancelRoute = returnUrl ?? (scopedTenantId
        ? routePaths.adminTenantGovernance.replace(':tenantId', scopedTenantId)
        : routePaths.adminGovernance);
    const steps = useMemo(() => governancePolicyCreateSteps(mode), [ mode ]);

    // Submission tracking for the Abandoned-event suppression in the telemetry tracker.
    // - `submittingRef` is set while the submit handler is in flight. It covers the unmount
    //   that happens when the handler navigates away mid-await (the common success path), and
    //   also avoids firing Abandoned if the user navigates externally during a slow submit.
    // - `savedRef` is set only after the handler resolves successfully, so a save that never
    //   completes leaves the wizard's "saved" status correct for downstream consumers.
    const submittingRef = useRef(false);
    const savedRef = useRef(false);
    const wrappedOnSubmitHandler = useCallback(async (data: GovernancePolicyFormData) => {
        submittingRef.current = true;
        try {
            await onSubmitHandler(data);
            savedRef.current = true;
        } finally {
            submittingRef.current = false;
        }
    }, [ onSubmitHandler ]);

    return (
        <UiWizard
            totalSteps={steps.length}
            steps={steps}
            cancelRoute={cancelRoute}
            stepperVariant="icon"
            formId="governance-policy-create-form"
            onSubmitHandler={wrappedOnSubmitHandler}
            initialStep={initialStep}
        >
            <GovernanceWizardTelemetryTracker
                mode={mode}
                savedRef={savedRef}
                submittingRef={submittingRef} />
        </UiWizard>
    );
};
